import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Trophy, MapPin, Phone, Mail, User, Loader2, CheckCircle, PartyPopper, Sparkles } from 'lucide-react';
import SEO, { competitionSchema } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WettbewerbPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    city: '',
    newsletter_confirmed: false
  });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/competition/status`);
      if (res.ok) setStatus(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/competition/enter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data);
      } else {
        setError(data.detail || 'Fehler bei der Anmeldung');
      }
    } catch (e) {
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" />
      </div>
    );
  }

  if (!status?.active) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <Trophy className="w-16 h-16 mx-auto text-[hsl(var(--muted-foreground))] mb-6" />
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] mb-4">Kein aktiver Wettbewerb</h1>
          <p className="text-[hsl(var(--muted-foreground))]">Aktuell läuft kein Wettbewerb. Schauen Sie bald wieder vorbei!</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <div className="max-w-2xl mx-auto px-4 py-20">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
            <Card className="border-2 border-emerald-200 bg-emerald-50/50">
              <CardContent className="pt-10 pb-10 text-center space-y-4">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-emerald-800">Teilnahme bestätigt!</h2>
                <p className="text-lg text-emerald-700">
                  Sie sind Teilnehmer Nr. <span className="font-bold text-2xl">{success.entry_number}</span>
                </p>
                {success.auto_winner && (
                  <motion.div
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: 'spring' }}
                    className="mt-6 p-6 bg-amber-50 border-2 border-amber-300 rounded-xl"
                  >
                    <PartyPopper className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                    <p className="text-lg font-bold text-amber-800">100 Teilnehmer erreicht! Ein Gewinner wurde ausgelost!</p>
                  </motion.div>
                )}
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-4">
                  {success.message}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO
        title="Wettbewerb – 1 Woche GRATIS Werbefläche"
        description="Nehmen Sie an unserem Wettbewerb teil und gewinnen Sie 1 Woche GRATIS Werbefläche im Zürcher Oberland. Ab 100 Teilnehmern wird automatisch ein Gewinner ausgelost!"
        canonicalPath="/wettbewerb"
        structuredData={competitionSchema()}
      />
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Jetzt mitmachen & gewinnen!
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))] mb-4">
            {status.title}
          </h1>
          <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
            {status.description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-8">
          {/* Prize Info */}
          <div className="md:col-span-2 space-y-4">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <Card className="border-2 border-[hsl(var(--primary)/0.3)] bg-[hsl(var(--primary)/0.03)]">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[hsl(var(--primary)/0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-5 h-5 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[hsl(var(--foreground))]">Hauptgewinn</h3>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">1 Woche GRATIS Werbefläche</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[hsl(var(--foreground))]">Automatische Verlosung</h3>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Ab 100 Teilnehmern wird automatisch ein Gewinner ausgelost</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Bereits <span className="font-bold text-[hsl(var(--foreground))] text-lg">{status.total_participants}</span> Teilnehmer registriert
                </p>
                <div className="mt-3">
                  <div className="h-2 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[hsl(var(--primary))] rounded-full transition-all"
                      style={{ width: `${Math.min(100, status.total_participants)}%` }}
                    />
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    {status.total_participants >= 100
                      ? 'Verlosung wird ausgelöst!'
                      : `Noch ${100 - status.total_participants} Teilnehmer bis zur automatischen Verlosung`}
                  </p>
                </div>

                {status.last_winner_name && (
                  <div className="mt-4 flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Trophy className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-amber-600 font-medium">Letzter Gewinner</p>
                      <p className="text-sm font-bold text-amber-800">{status.last_winner_name}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Registration Form */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="md:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>Jetzt anmelden</CardTitle>
                <CardDescription>Füllen Sie das Formular aus, um am Wettbewerb teilzunehmen.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Vorname *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <Input
                          id="first_name"
                          required
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          className="pl-10"
                          placeholder="Max"
                          data-testid="comp-first-name"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nachname *</Label>
                      <Input
                        id="last_name"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        placeholder="Muster"
                        data-testid="comp-last-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp_email">E-Mail *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <Input
                        id="comp_email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10"
                        placeholder="max@beispiel.ch"
                        data-testid="comp-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <Input
                        id="mobile"
                        type="tel"
                        required
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                        className="pl-10"
                        placeholder="+41 79 123 45 67"
                        data-testid="comp-mobile"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">Wohnort *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <Input
                        id="city"
                        required
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="pl-10"
                        placeholder="Wetzikon"
                        data-testid="comp-city"
                      />
                    </div>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Wir prüfen, ob Sie im 20km-Umkreis wohnen.</p>
                  </div>

                  <div className="flex items-start gap-3 p-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--accent)/0.3)]">
                    <Checkbox
                      id="newsletter"
                      checked={formData.newsletter_confirmed}
                      onCheckedChange={(checked) => setFormData({ ...formData, newsletter_confirmed: !!checked })}
                      data-testid="comp-newsletter-checkbox"
                    />
                    <div>
                      <Label htmlFor="newsletter" className="text-sm font-medium cursor-pointer">
                        Newsletter abonnieren *
                      </Label>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                        Ich möchte per E-Mail über Ergebnisse und Angebote informiert werden. Ohne Newsletter-Bestätigung ist eine Teilnahme nicht möglich.
                      </p>
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="comp-error">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={submitting || !formData.newsletter_confirmed}
                    data-testid="comp-submit-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird registriert...</>
                    ) : (
                      <><Trophy className="w-4 h-4 mr-2" /> Am Wettbewerb teilnehmen</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WettbewerbPage;
